import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { C, S } from '../../utils/styles';
import { useToast } from '../Toast';
import { fmt } from '../../utils/formatters';
import HelpSection from './HelpSection';

export default function RevenueCommissionsEditor() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();
  const [draft, setDraft] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (config) {
      setDraft({
        spreadAllocation: config.revenueSettings?.spreadAllocation || [
          { category: 'Broker Commission', percentage: 15 },
          { category: 'Vendor Platform', percentage: 25 },
          { category: 'Hospitalization Underwriting', percentage: 30 },
          { category: 'HealthCues Margin', percentage: 30 },
        ],
        showAllocationInReports: config.revenueSettings?.showAllocationInReports !== false,
        showAllocationInEmployerPDF: config.revenueSettings?.showAllocationInEmployerPDF || false,
        benefitValues: config.revenueSettings?.benefitValues || [
          { benefit: 'Telemedicine (24/7, $0 copay)', value: 75 },
          { benefit: 'Prescription Program (70+ meds)', value: 30 },
          { benefit: 'Health Coaching & Goals', value: 25 },
          { benefit: 'DNA & Biometric Screening', value: 15 },
          { benefit: 'Behavioral Health / Teletherapy', value: 40 },
          { benefit: 'Personal Training & Nutrition', value: 15 },
        ],
        showBenefitValues: config.revenueSettings?.showBenefitValues !== false,
      });
    }
  }, [config]);

  const handleAllocationChange = (index, field, value) => {
    const updated = [...draft.spreadAllocation];
    updated[index][field] = field === 'percentage' ? (parseFloat(value) || 0) : value;
    setDraft(prev => ({ ...prev, spreadAllocation: updated }));
    setHasChanges(true);
  };

  const addAllocationCategory = () => {
    setDraft(prev => ({
      ...prev,
      spreadAllocation: [...prev.spreadAllocation, { category: 'New Category', percentage: 0 }]
    }));
    setHasChanges(true);
  };

  const removeAllocationCategory = (index) => {
    setDraft(prev => ({
      ...prev,
      spreadAllocation: prev.spreadAllocation.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const handleBenefitChange = (index, field, value) => {
    const updated = [...draft.benefitValues];
    updated[index][field] = field === 'value' ? (parseFloat(value) || 0) : value;
    setDraft(prev => ({ ...prev, benefitValues: updated }));
    setHasChanges(true);
  };

  const addBenefit = () => {
    setDraft(prev => ({
      ...prev,
      benefitValues: [...prev.benefitValues, { benefit: 'New Benefit', value: 0 }]
    }));
    setHasChanges(true);
  };

  const removeBenefit = (index) => {
    setDraft(prev => ({
      ...prev,
      benefitValues: prev.benefitValues.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const resetToDefaults = () => {
    setDraft({
      spreadAllocation: [
        { category: 'Broker Commission', percentage: 15 },
        { category: 'Vendor Platform', percentage: 25 },
        { category: 'Hospitalization Underwriting', percentage: 30 },
        { category: 'HealthCues Administration', percentage: 30 },
      ],
      showAllocationInReports: true,
      showAllocationInEmployerPDF: false,
      benefitValues: [
        { benefit: 'Telemedicine (24/7, $0 copay)', value: 75 },
        { benefit: 'Prescription Program (70+ meds)', value: 30 },
        { benefit: 'Health Coaching & Goals', value: 25 },
        { benefit: 'DNA & Biometric Screening', value: 15 },
        { benefit: 'Behavioral Health / Teletherapy', value: 40 },
        { benefit: 'Personal Training & Nutrition', value: 15 },
      ],
      showBenefitValues: true,
    });
    setHasChanges(true);
  };

  const save = async () => {
    try {
      await updateConfig({
        revenueSettings: draft
      });
      setHasChanges(false);
      showToast('Revenue & commissions saved successfully', 'success');
    } catch (err) {
      showToast('Failed to save revenue & commissions', 'error');
    }
  };

  const cancel = () => {
    setDraft({
      spreadAllocation: config.revenueSettings?.spreadAllocation || [
        { category: 'Broker Commission', percentage: 15 },
        { category: 'Vendor Platform', percentage: 25 },
        { category: 'Hospitalization Underwriting', percentage: 30 },
        { category: 'HealthCues Administration', percentage: 30 },
      ],
      showAllocationInReports: config.revenueSettings?.showAllocationInReports !== false,
      showAllocationInEmployerPDF: config.revenueSettings?.showAllocationInEmployerPDF || false,
      benefitValues: config.revenueSettings?.benefitValues || [
        { benefit: 'Telemedicine (24/7, $0 copay)', value: 75 },
        { benefit: 'Prescription Program (70+ meds)', value: 30 },
        { benefit: 'Health Coaching & Goals', value: 25 },
        { benefit: 'DNA & Biometric Screening', value: 15 },
        { benefit: 'Behavioral Health / Teletherapy', value: 40 },
        { benefit: 'Personal Training & Nutrition', value: 15 },
      ],
      showBenefitValues: config.revenueSettings?.showBenefitValues !== false,
    });
    setHasChanges(false);
  };

  if (!draft) return <div>Loading...</div>;

  const totalAllocation = draft.spreadAllocation.reduce((sum, item) => sum + item.percentage, 0);
  const allocationValid = Math.abs(totalAllocation - 100) < 0.01;
  const referenceSpread = 200;
  const totalBenefitValue = draft.benefitValues.reduce((sum, item) => sum + item.value, 0);

  const helpSections = [
    {
      heading: 'What is the Spread?',
      content: 'The monthly spread is the difference between the premium paid by the employer and the expected claims payout: Spread = Premium - Expected Claims. This spread covers broker commissions, platform costs, underwriting reserves, and company margin.'
    },
    {
      heading: 'Spread Allocation',
      content: 'Defines how the monthly spread is divided among stakeholders:',
      impacts: [
        'Broker Commission: % going to the selling broker (default 15%)',
        'Vendor Platform: Technology and administrative fees (default 25%)',
        'Hospitalization Underwriting: Reserve fund for claim risk (default 30%)',
        'HealthCues Administration: Company administrative costs and overhead (default 30%)',
        'Must total exactly 100% — the system validates this before saving'
      ]
    },
    {
      heading: 'Where Allocation Appears',
      content: (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li><strong>Internal Reports:</strong> Economics tab shows per-plan spread breakdown (toggle on by default)</li>
          <li><strong>Employer PDFs:</strong> Optionally show allocation transparency (toggle off by default for competitive reasons)</li>
          <li><strong>Broker Commissions:</strong> Used to calculate monthly/annual commission statements</li>
        </ul>
      )
    },
    {
      heading: 'Benefit Value Attribution',
      content: 'Estimated monthly value of non-cash benefits included in the plan (e.g., telemedicine, prescriptions, health coaching). These help communicate total program value beyond just paycheck savings.',
      impacts: [
        'Employee Materials: Shows "You save $X in taxes + $Y in benefits" messaging',
        'PDF Reports: Includes benefit value in total value proposition',
        'Disclaimer auto-appended: "Values are estimates based on market rates"'
      ]
    },
    {
      heading: 'Customization Tips',
      content: (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>Adjust broker commission % per contract type (renewal vs new client)</li>
          <li>Add custom categories (e.g., "Partner Referral Fee")</li>
          <li>Set "Show in employer PDF" based on market positioning strategy</li>
          <li>Update benefit values annually as market rates change</li>
        </ul>
      )
    }
  ];

  return (
    <div style={S.card}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.text }}>Revenue & Commissions</h2>
      <p style={{ fontSize: 13, color: C.textMid, marginBottom: 20 }}>Define how the spread is allocated and how benefit values are presented to employees.</p>

      <HelpSection title="How does spread allocation work?" sections={helpSections} />

      {/* Spread Allocation */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Spread Allocation</h3>
        <p style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>The monthly spread (Premium − Claim) is divided among. Allocations must total 100%.</p>
        
        <div style={{ 
          border: `1px solid ${C.border}`, 
          borderRadius: 8, 
          overflow: 'hidden',
          marginBottom: 12
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 1fr 1fr 40px',
            background: C.bg,
            padding: '8px 12px',
            borderBottom: `1px solid ${C.border}`,
            fontSize: 11,
            fontWeight: 700,
            color: C.textMid,
            textTransform: 'uppercase'
          }}>
            <div>Category</div>
            <div style={{ textAlign: 'center' }}>%</div>
            <div style={{ textAlign: 'right' }}>On ${referenceSpread} spread</div>
            <div></div>
          </div>
          {draft.spreadAllocation.map((item, idx) => (
            <div 
              key={idx}
              style={{ 
                display: 'grid', 
                gridTemplateColumns: '2fr 1fr 1fr 40px',
                padding: '10px 12px',
                borderBottom: idx < draft.spreadAllocation.length - 1 ? `1px solid ${C.borderLight}` : 'none',
                alignItems: 'center'
              }}
            >
              <input 
                type="text"
                value={item.category}
                onChange={e => handleAllocationChange(idx, 'category', e.target.value)}
                style={{ ...S.input, padding: '6px 10px', fontSize: 13 }}
              />
              <input 
                type="number"
                value={item.percentage}
                onChange={e => handleAllocationChange(idx, 'percentage', e.target.value)}
                style={{ ...S.input, padding: '6px 10px', fontSize: 13, textAlign: 'center' }}
                min="0"
                max="100"
                step="0.1"
              />
              <div style={{ textAlign: 'right', fontSize: 13, ...S.mono, fontWeight: 600 }}>
                {fmt((referenceSpread * item.percentage) / 100)}/mo
              </div>
              <button
                onClick={() => removeAllocationCategory(idx)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: C.red,
                  cursor: 'pointer',
                  fontSize: 16
                }}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 1fr 1fr 40px',
            padding: '10px 12px',
            background: C.bg,
            borderTop: `2px solid ${C.border}`,
            fontWeight: 700
          }}>
            <div>Total</div>
            <div style={{ 
              textAlign: 'center', 
              color: allocationValid ? C.green : C.red,
              fontSize: 14
            }}>
              {totalAllocation.toFixed(1)}%
            </div>
            <div style={{ textAlign: 'right', ...S.mono }}>
              {fmt(referenceSpread)}/mo
            </div>
            <div></div>
          </div>
        </div>

        {!allocationValid && (
          <div style={{ 
            padding: 10, 
            background: C.redLight, 
            border: `1px solid ${C.red}`,
            borderRadius: 6,
            fontSize: 12,
            color: C.red,
            marginBottom: 12
          }}>
            ⚠️ Total allocation must equal 100% (currently {totalAllocation.toFixed(1)}%)
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={addAllocationCategory} style={S.buttonSecondary}>➕ Add Category</button>
          <button onClick={resetToDefaults} style={S.buttonSecondary}>Reset to Defaults</button>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 8 }}>
            <input 
              type="checkbox"
              checked={draft.showAllocationInReports}
              onChange={e => {
                setDraft(prev => ({ ...prev, showAllocationInReports: e.target.checked }));
                setHasChanges(true);
              }}
            />
            Show allocation breakdown in internal reports & dashboards
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input 
              type="checkbox"
              checked={draft.showAllocationInEmployerPDF}
              onChange={e => {
                setDraft(prev => ({ ...prev, showAllocationInEmployerPDF: e.target.checked }));
                setHasChanges(true);
              }}
            />
            Show allocation breakdown in employer-facing PDF report
          </label>
        </div>
      </div>

      {/* Benefit Value Attribution */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={S.heading}>Benefit Value Attribution</h3>
        <p style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>Assign estimated monthly values to non-cash benefits included in the program. Shown to employees alongside their financial savings to communicate total value.</p>
        
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 12 }}>
          <input 
            type="checkbox"
            checked={draft.showBenefitValues}
            onChange={e => {
              setDraft(prev => ({ ...prev, showBenefitValues: e.target.checked }));
              setHasChanges(true);
            }}
          />
          Show benefit value attribution in employee materials
        </label>

        <div style={{ 
          border: `1px solid ${C.border}`, 
          borderRadius: 8, 
          overflow: 'hidden',
          marginBottom: 12
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 1fr 40px',
            background: C.bg,
            padding: '8px 12px',
            borderBottom: `1px solid ${C.border}`,
            fontSize: 11,
            fontWeight: 700,
            color: C.textMid,
            textTransform: 'uppercase'
          }}>
            <div>Benefit</div>
            <div style={{ textAlign: 'right' }}>Est. Value/mo</div>
            <div></div>
          </div>
          {draft.benefitValues.map((item, idx) => (
            <div 
              key={idx}
              style={{ 
                display: 'grid', 
                gridTemplateColumns: '2fr 1fr 40px',
                padding: '10px 12px',
                borderBottom: idx < draft.benefitValues.length - 1 ? `1px solid ${C.borderLight}` : 'none',
                alignItems: 'center'
              }}
            >
              <input 
                type="text"
                value={item.benefit}
                onChange={e => handleBenefitChange(idx, 'benefit', e.target.value)}
                style={{ ...S.input, padding: '6px 10px', fontSize: 13 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 13 }}>$</span>
                <input 
                  type="number"
                  value={item.value}
                  onChange={e => handleBenefitChange(idx, 'value', e.target.value)}
                  style={{ ...S.input, padding: '6px 10px', fontSize: 13, width: 100, textAlign: 'right' }}
                  min="0"
                  step="0.01"
                />
              </div>
              <button
                onClick={() => removeBenefit(idx)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: C.red,
                  cursor: 'pointer',
                  fontSize: 16
                }}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '2fr 1fr 40px',
            padding: '10px 12px',
            background: C.bg,
            borderTop: `2px solid ${C.border}`,
            fontWeight: 700
          }}>
            <div>Total Monthly Benefit Value</div>
            <div style={{ textAlign: 'right', ...S.mono, fontSize: 14 }}>
              {fmt(totalBenefitValue)}
            </div>
            <div></div>
          </div>
        </div>

        <button onClick={addBenefit} style={S.buttonSecondary}>➕ Add Benefit</button>

        <div style={{ 
          marginTop: 16, 
          padding: 12, 
          background: C.bg, 
          borderRadius: 8,
          fontSize: 12,
          color: C.textMid,
          lineHeight: 1.6
        }}>
          <strong>Where this appears:</strong>
          <ul style={{ marginTop: 8, marginLeft: 20 }}>
            <li>Employee benefits page: "Plus ${totalBenefitValue}/mo in health benefits"</li>
            <li>PDF report: "Total value: $X take-home + ${totalBenefitValue} benefits"</li>
            <li>Individual tab: optional display below paycheck breakdown</li>
          </ul>
          <div style={{ marginTop: 12, fontStyle: 'italic' }}>
            <strong>Disclaimer (auto-appended):</strong> "Benefit values are estimated based on comparable market rates for standalone services. Actual utilization and value may vary."
          </div>
        </div>
      </div>

      {/* Save/Cancel */}
      {hasChanges && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={cancel} style={S.buttonSecondary}>Cancel</button>
          <button data-testid="save-revenue-btn" onClick={save} style={S.buttonPrimary}>Save Changes</button>
        </div>
      )}
    </div>
  );
}
